import { Link, useLocation } from 'react-router-dom';

import { format, parseISO } from 'date-fns';
import { observer } from 'mobx-react-lite';
import Search from 'shared/elements/Search';
import FormDropDownMulti from 'shared/forms/FormDropDownMulti';
import arrow from 'shared/static/images/ic_arrow_right.svg';
import styled from 'styled-components';

import { useEnterpriseFilters } from 'hooks/use-enterprise-filters';
import useDocumentTitle from 'hooks/useDocumentTitle';
import { useStore } from 'models/stores';

import MainHeader from './MainHeader';
import NoAvailableJobs from './NoAvailableJobs';

function JobListEnterprise() {
  const { jobStore, uiStore, companyStore } = useStore();
  const { current } = companyStore;
  const { state, jobs } = jobStore;
  uiStore.clearSelectedJob();
  useDocumentTitle();

  const filters = useEnterpriseFilters(jobs);

  let searchFilteredJobs = jobs;
  if (filters.searchString) {
    searchFilteredJobs = jobs.filter((job) => {
      return job.searchableTitle.includes(filters.searchString);
    });
  }

  const isFilteringStatuses = Object.keys(filters.selectedStatuses).some(
    (key) => filters.selectedStatuses[key]
  );

  const filteredJobs = searchFilteredJobs.filter((job) => {
    if (filters.selectedDepartments.length > 0) {
      if (!filters.selectedDepartments.includes(String(job.department))) return false;
    }
    if (isFilteringStatuses) {
      const filterable = filters.statusesObj;
      if (!filterable.full_time && !filterable.part_time) return true;
      return filterable[job.status] === true;
    }
    return true;
  });

  const jobsByDepartment = filteredJobs.reduce((result, job) => {
    if (result[String(job.department)] === undefined) {
      result[String(job.department)] = [];
    }
    result[String(job.department)].push(job);
    return result;
  }, {});

  const hasJobsWithoutDepartment = filteredJobs.some((job) => job.department === null);

  return (
    <>
      <MainHeader>
        {jobs.length > 0 ? (
          <Filters
            hasNotSpecifiedDepartment={jobs.some((job) => job.department === null)}
            filters={filters}
            company={current}
          />
        ) : null}
      </MainHeader>
      <OpeningsHeader>
        {state === 'loaded' && jobs.length > 0 && (
          <span>
            {jobs.length} {uiStore.t(`vacancies_${jobs.length === 1 ? 'singular' : 'plural'}`)}
          </span>
        )}
      </OpeningsHeader>
      {state === 'loaded' && jobs.length === 0 && <NoAvailableJobs lang={uiStore.language} />}
      <JobListContainer>
        {current.departments.map((department) => (
          <JobDepartment
            department={department.id}
            jobs={jobsByDepartment[department.id]}
            key={department.id}
          />
        ))}
        {hasJobsWithoutDepartment && (
          <JobDepartment department={null} jobs={jobsByDepartment[null]} />
        )}
      </JobListContainer>
    </>
  );
}
export default observer(JobListEnterprise);

const JobDepartment = observer(({ jobs, department }) => {
  const { uiStore, companyStore } = useStore();
  const { t } = uiStore;
  const { departmentLabels } = companyStore.current;
  const departmentLabel = departmentLabels.find((label) => label.language === uiStore.language);
  if (!jobs) return null;
  const translatedDepartment = companyStore.current.departments.find(
    (d) => d.id === Number(department)
  );
  return (
    <>
      <DepartmentHeader>
        <div>
          {translatedDepartment
            ? translatedDepartment.translatedName
            : t('department_title_unknown', departmentLabel?.title)}
        </div>
        {t('deadline_display')}
      </DepartmentHeader>
      {jobs.map((j) => (
        <JobEntry j={j} t={t} key={j.id} />
      ))}
    </>
  );
});

const JobEntry = observer(({ j, t }) => {
  const dl = j.deadline ? format(parseISO(j.deadline), 'dd.MM.yyyy') : '-';
  const search = useLocation().search;
  const title = `${j.title} - ${j.locationDisplay}`;
  return (
    <Link to={{ pathname: String(j.id), search }}>
      <JobRow>
        <div>
          <div>{title}</div>
          <JobRowDeadline>
            {t('deadline_display')}: {dl}
          </JobRowDeadline>
        </div>
        <DateArrowContainer>
          <span className="arrow">
            <img alt="arrow right" src={arrow} />
          </span>
          <span className="deadline">{dl}</span>
        </DateArrowContainer>
      </JobRow>
    </Link>
  );
});

const Filters = observer(({ company, filters, hasNotSpecifiedDepartment }) => {
  const { uiStore, companyStore } = useStore();
  const { departmentLabels } = companyStore.current;
  const departmentLabel = departmentLabels.find((label) => label.language === uiStore.language);
  return (
    <FilterContainer>
      {company.selectableDepartments.length > 0 && (
        <FormDropDownMulti
          items={
            hasNotSpecifiedDepartment
              ? [
                  ...company.selectableDepartments,
                  { display: uiStore.t('department_title_unknown', ''), value: 'null' },
                ]
              : company.selectableDepartments
          }
          title={departmentLabel?.title}
          onChange={filters.selectDepartment}
          defaultSelected={filters.departmentsObj}
        />
      )}
      <FormDropDownMulti
        items={filters.statusTypes}
        title={uiStore.t('jobStatus_title')}
        onChange={filters.selectStatus}
        defaultSelected={filters.statusesObj}
      />
      <div id="search">
        <Search
          placeholder={uiStore.t('filter_search_placeholder')}
          onChange={(e) => filters.setSearchValue(e.target.value)}
          value={filters.searchValue}
        />
      </div>
    </FilterContainer>
  );
});

const JobListContainer = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  padding: 0 20px;
`;

const FilterContainer = styled.div`
  display: flex;
  flex-grow: 1;
  justify-content: flex-end;
  align-items: center;
  max-width: 100%;
  > div {
    width: 33%;
    max-width: 33%;
  }

  > div:last-child {
    max-width: initial;
  }

  > div:not(:last-child) {
    margin-right: 20px;
  }

  @media (max-width: 600px) {
    flex-direction: column;
    > div {
      width: 100%;
      margin: 0;
      max-width: 100%;
    }
    > div:not(:last-child) {
      margin-right: 0;
    }

    > div:not(:first-child) {
      margin-top: 10px;
    }
  }

  #search > div > div {
    margin: 0px !important;
  }
`;

const DateSorter = styled.div`
  @media (max-width: 600px) {
    display: none;
  }
  :hover {
    cursor: pointer;
  }

  :after {
    transition: all 200ms ease-in-out;
    content: '';
    width: 0;
    height: 0;
    position: absolute;
    top: 50%;
    margin-left: 5px;
    margin-top: -4px;
    border-width: 0 5px 6px 5px;
    transform: ${(p) => (p.desc ? 'rotate(0deg)' : 'rotate(180deg) translateY(-1px)')};
    border-style: solid;
    border-color: #81839b transparent;
    opacity: 0.65;
  }
`;

const DepartmentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  position: relative;
  margin: 30px 20px 10px 0;
  > div:nth-child(1) {
    font-weight: bold;
    color: ${(p) => p.theme.categoryHeaderColor};
  }
`;

const DateArrowContainer = styled.div`
  height: 16px;
  display: block;
  margin-right: 20px;
  @media (max-width: 600px) {
    ${(p) => !p.hovering && 'display: none'};
  }
`;

const JobRowDeadline = styled.div`
  font-size: 12px;
  color: ${(p) => p.theme.textColor};
  opacity: 0.65;
  display: none;

  @media (max-width: 600px) {
    display: block;
  }
`;

const JobRow = styled.div`
  border-top: 1px solid #e0e0e0;
  transition: all 100ms ease-out;
  padding: 30px 20px;
  display: flex;
  justify-content: space-between;
  color: #404040;
  align-items: center;
  font-weight: normal;

  :hover {
    background-color: ${(p) => p.theme.listBgHoverColor};
    color: ${(p) => p.theme.headingHoverColor};
    cursor: pointer;
  }

  @media (max-width: 600px) {
    padding: 12px;
  }

  .arrow {
    display: none;
  }

  .deadline {
    display: inline;
  }

  :hover {
    .arrow {
      display: inline;
    }
    .deadline {
      display: none;
    }
  }
`;

const OpeningsHeader = styled.h2`
  color: ${(p) => p.theme.headingColor};
  font-weight: bold;
  font-size: 24px;
  margin-top: 30px;
  margin-bottom: 0px;
  @media (max-width: 600px) {
    font-size: 18px;
  }
  padding: 0 20px;
`;
